<template>
	<checkout-block>
		<checkout-block-header>Faktureringsoplysninger</checkout-block-header>

		<checkout-block-body class="text-xs sm:text-base">
			<div
				v-if="!showNotFilled && basket.TotalPrice == 0"
				class="text-xs text-gray-900 mb-6"
			>
				Den samlede pris for dine valgte tilmeldinger er DKR 0,-<br />
				Udfyld oplysningerne, og gennemfør tilmeldingen. Du vil ikke blive
				faktureret for købet.
			</div>
			<div
				v-if="showNotFilled"
				class="border border-dashed border-gray-300 p-6"
			>
				Udfyld venligst først dine deltageroplysninger
			</div>
			<div v-else-if="showChoices">
				<div class="inline-flex h-5 items-center">
					<div class="font-bold">Hvem skal modtage fakturaen?</div>

					<div
						v-if="
							hasTriedToCheckout &&
								!basket.OtherBillingSelected &&
								selectedParticipantIndex > -1
						"
						class="ml-4"
					>
						<svg
							width="20"
							height="20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<rect width="20" height="20" rx="10" fill="#4E9036" />
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M8.472 12.195L14.667 6l.943.943-7.138 7.138-3.805-3.805.943-.943 2.862 2.862z"
								fill="#fff"
							/>
						</svg>
					</div>
				</div>

				<div v-for="(participant, index) in distinctParticipants" :key="index">
					<label
						class="inline-flex items-center font-normal cursor-pointer"
					>
						<input
							type="radio"
							class="form-radio text-top focus:outline-none"
							name="billingParticipant"
							:value="index"
							v-model="selectedParticipantIndex"
							@change="selectParticipant"
						/>
						<span class="ml-2"
							>{{ participant.FirstName }} {{ participant.LastName }}</span
						>
					</label>
				</div>

				<div>
					<label
						class="inline-flex items-center font-normal cursor-pointer"
					>
						<input
							type="radio"
							class="form-radio text-top focus:outline-none"
							name="billingParticipant"
							value="other"
							:checked="basket.OtherBillingSelected"
							@change="toggleOtherBilling"
						/>
						<span class="ml-2">Anden</span>
					</label>
				</div>

				<transition
					enter-active-class="transition-all duration-3s timing-ease"
					leave-active-class="transition-all duration-3s timing-ease"
					enter-class="opacity-0 scale-y-0 -translate-y-full"
					enter-to-class="opacity-1 scale-y-100 translate-y-0"
					leave-class="opacity-1 scale-y-100 translate-y-0"
					leave-to-class="opacity-0 scale-y-0 -translate-y-full"
				>
					<div
						v-if="
							hasTriedToCheckout &&
								!basket.OtherBillingSelected &&
								selectedParticipantIndex == -1
						"
						class="bg-red-700 inline-block mt-2 text-white p-1 rounded-sm font-normal text-micro sm:text-xs"
					>
						Du skal vælge en fakturamodtager
					</div>
				</transition>

				<div
					v-if="basket.OtherBillingSelected"
					class="border bg-white text-xs sm:text-base text-black p-4 sm:p-8 mt-2 sm:mt-6 -ml-2 sm:ml-0 border-gray-300"
					:class="{
						'border-red-700': hasTriedToCheckout && missingFields.length > 0,
						'border-gray-300':
							!hasTriedToCheckout || missingFields.length == 0
					}"
				>
					<div class="flex flex-wrap -ml-2 mt-6">
						<participant-field
							class="w-full md:w-1/2"
							label="Fornavn"
							v-model="basket.Billing.FirstName"
							:required="true"
							:show-valid="isValid('FirstName')"
							:show-required-message="isMissing('FirstName')"
							@blur="validate('FirstName')"
							@keyup="[saveBillingData(), validate('FirstName', true)]"
							@change="[saveBillingData(), validate('FirstName')]"
						/>
						<participant-field
							class="w-full mt-6 md:mt-0 md:w-1/2"
							label="Efternavn"
							v-model="basket.Billing.LastName"
							:required="true"
							:show-valid="isValid('LastName')"
							:show-required-message="isMissing('LastName')"
							@blur="validate('LastName')"
							@keyup="[saveBillingData(), validate('LastName', true)]"
							@change="[saveBillingData(), validate('LastName')]"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							type="email"
							label="E-mail"
							v-model="basket.Billing.Email"
							:required="true"
							:show-valid="isValid('Email') && isPatternValid('Email')"
							:show-required-message="isMissing('Email')"
							:show-pattern-message="isPatternMissing('Email')"
							pattern-error-message="Indtast venligst i formatet xx@xx.xx"
							@blur="
								[
									validate('Email', false),
									validatePattern('Email', false, emailPattern)
								]
							"
							@keyup="
								[
									saveBillingData(),
									validate('Email', true)
								]
							"
							@change="
								[
									saveBillingData(),
									validate('Email', false)
								]
							"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							type="tel"
							label="Telefon"
							v-model="basket.Billing.Mobile"
							:required="true"
							:show-valid="isValid('Mobile') && isPatternValid('Mobile')"
							:show-required-message="isMissing('Mobile')"
							:show-pattern-message="isPatternMissing('Mobile')"
							pattern-error-message="Indtast venligst 8 cifre"
							@blur="
								[
									saveBillingData(),
									validate('Mobile', false),
									validatePattern('Mobile', false, phonePattern)
								]
							"
							@keyup="
								[
									saveBillingData(),
									validate('Mobile', true)
								]
							"
							@change="
								[
									saveBillingData(),
									validate('Mobile', false)
								]
							"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							v-model="basket.Billing.CompanyName"
							:required="false"
							@change="[changed]"
						>
							<span class="pb-2 font-bold"
								>Firma <span class="font-normal">(valgfri)</span></span
							>
						</participant-field>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							label="Adresse"
							v-model="basket.Billing.Address"
							:required="true"
							:show-valid="isValid('Address')"
							:show-required-message="isMissing('Address')"
							@blur="validate('Address')"
							@keyup="[saveBillingData(), validate('Address', true)]"
							@change="[saveBillingData(), validate('Address')]"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							class="md:w-1/3"
							label="Post nr."
							v-model="basket.Billing.Zip"
							:required="true"
							:show-valid="isValid('Zip') && isPatternValid('Zip')"
							:show-required-message="isMissing('Zip')"
							pattern-error-message="Indtast venligst 4 cifre"
							:show-pattern-message="isPatternMissing('Zip')"
							@blur="
								[
									validate('Zip'),
									validatePattern('Zip', false, zipPattern)
								]
							"
							@keyup="
								[
									saveBillingData(),
									validate('Zip', true)
								]
							"
							@change="
								[
									saveBillingData(),
									validate('Zip')
								]
							"
						/>
						<participant-field
							class="md:w-2/3"
							label="By"
							v-model="basket.Billing.City"
							:required="true"
							:show-valid="isValid('City')"
							:show-required-message="isMissing('City')"
							@blur="validate('City')"
							@keyup="[saveBillingData(), validate('City', true)]"
							@change="[saveBillingData(), validate('City')]"
						/>
					</div>
				</div>
			</div>
			<div v-else>
				<div class="font-bold">Hvem skal modtage fakturaen?</div>

				<div
					class="border bg-white text-xs sm:text-base text-black p-4 sm:p-8 mt-2 sm:mt-6 -ml-2 sm:ml-0 border-gray-300"
					:class="{
						'border-red-700': hasTriedToCheckout && missingFields.length > 0,
						'border-gray-300':
							!hasTriedToCheckout || missingFields.length == 0
					}"
				>
					<div class="flex flex-wrap -ml-2 mt-6">
						<participant-field
							class="w-full md:w-1/2"
							label="Fornavn"
							v-model="basket.Billing.FirstName"
							:required="true"
							:show-valid="isValid('FirstName')"
							:show-required-message="isMissing('FirstName')"
							@blur="validate('FirstName')"
							@keyup="[saveBillingData(), validate('FirstName', true)]"
							@change="[saveBillingData(), validate('FirstName')]"
						/>
						<participant-field
							class="w-full mt-6 md:mt-0 md:w-1/2"
							label="Efternavn"
							v-model="basket.Billing.LastName"
							:required="true"
							:show-valid="isValid('LastName')"
							:show-required-message="isMissing('LastName')"
							@blur="validate('LastName')"
							@keyup="[saveBillingData(), validate('LastName', true)]"
							@change="[saveBillingData(), validate('LastName')]"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							type="email"
							label="E-mail"
							v-model="basket.Billing.Email"
							:required="true"
							:show-valid="isValid('Email') && isPatternValid('Email')"
							:show-required-message="isMissing('Email')"
							:show-pattern-message="isPatternMissing('Email')"
							pattern-error-message="Indtast venligst i formatet xx@xx.xx"
							@blur="
								[
									saveBillingData(),
									validate('Email'),
									validatePattern('Email', false, emailPattern)
								]
							"
							@keyup="
								[
									saveBillingData(),
									validate('Email', true)
								]
							"
							@change="
								[
									saveBillingData(),
									validate('Email')
								]
							"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							type="tel"
							label="Telefon"
							v-model="basket.Billing.Mobile"
							:required="true"
							:show-valid="isValid('Mobile') && isPatternValid('Mobile')"
							:show-required-message="isMissing('Mobile')"
							:show-pattern-message="isPatternMissing('Mobile')"
							pattern-error-message="Indtast venligst 8 cifre"
							@blur="
								[
									saveBillingData(),
									validate('Mobile'),
									validatePattern('Mobile', false, phonePattern)
								]
							"
							@keyup="
								[
									saveBillingData(),
									validate('Mobile', true),
								]
							"
							@change="
								[
									saveBillingData(),
									validate('Mobile')
								]
							"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							v-model="basket.Billing.CompanyName"
							:required="false"
							@change="[changed]"
						>
							<span class="pb-2 font-bold"
								>Firma <span class="font-normal">(valgfri)</span></span
							>
						</participant-field>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							label="Adresse"
							v-model="basket.Billing.Address"
							:required="true"
							:show-valid="isValid('Address')"
							:show-required-message="isMissing('Address')"
							@blur="validate('Address')"
							@keyup="[saveBillingData(), validate('Address', true)]"
							@change="[saveBillingData(), validate('Address')]"
						/>
					</div>

					<div class="flex -ml-2 mt-6">
						<participant-field
							class="md:w-1/3"
							label="Post nr."
							v-model="basket.Billing.Zip"
							:required="true"
							:show-valid="isValid('Zip') && isPatternValid('Zip')"
							:show-required-message="isMissing('Zip')"
							pattern-error-message="Indtast venligst 4 cifre"
							:show-pattern-message="isPatternMissing('Zip')"
							@blur="
								[
									saveBillingData(),
									validate('Zip'),
									validatePattern('Zip', false, zipPattern)
								]
							"
							@keyup="
								[
									saveBillingData(),
									validate('Zip', true)
								]
							"
							@change="
								[
									saveBillingData(),
									validate('Zip'),
									validatePattern('Zip', false, zipPattern)
								]
							"
						/>
						<participant-field
							class="md:w-2/3"
							label="By"
							v-model="basket.Billing.City"
							:required="true"
							:show-valid="isValid('City')"
							:show-required-message="isMissing('City')"
							@blur="validate('City')"
							@keyup="[saveBillingData(), validate('City', true)]"
							@change="[saveBillingData(), validate('City')]"
						/>
					</div>
				</div>
			</div>
		</checkout-block-body>
	</checkout-block>
</template>

<script>
import debounce from "lodash/debounce";
import ParticipantField from "./ParticipantField";
import { bus } from "../index";

export default {
	data: function() {
		return {
			validatedFields: [],
			validFields: [],
			validPatterns: [],
			missingFields: [],
			missingPatterns: [],
			selectedParticipantIndex: -1,
			zipPattern: /^\d{4}$/g,
			phonePattern: /^\d{8}$/g,
			emailPattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/g,
			dobPattern: /^(?:[012][0-9]|3[01])\/(?:0[0-9]|1[0-2])\/(?:19|20)[0-9]{2}$/g
		};
	},
	mounted: function() {
		this.selectedParticipantIndex = -1;
		let selectedParticipants = this.distinctParticipants.map(p =>
			this.participantIsSelected(p)
		);
		this.selectedParticipantIndex = selectedParticipants.indexOf(true);
		bus.$on("basketUpdated", data => {
			let selectedParticipants = this.distinctParticipants.map(p =>
				this.participantIsSelected(p)
			);
			this.selectedParticipantIndex = selectedParticipants.indexOf(true);

			if (
				this.selectedParticipantIndex == -1 &&
				this.basket.Billing &&
				this.basket.Billing.FirstName &&
				this.basket.Billing.LastName &&
				this.basket.Billing.Email
			) {
				this.basket.OtherBillingSelected = true;
			}
		});
	},
	components: {
		ParticipantField
	},
	computed: {
		hasTriedToCheckout: function() {
			return this.$store.getters.hasTriedToCheckout;
		},
		basket: function() {
			return this.$store.getters.basket;
		},
		distinctParticipants: function() {
			return this.$store.getters.distinctParticipants;
		},
		showChoices: function() {
			return this.distinctParticipants.length > 0;
		},
		showNotFilled: function() {
			return (
				this.basket.OrderLines.filter(ol => ol.ProductType == 2).length > 0 &&
				!this.showChoices
			);
		}
	},
	methods: {
		isMissing: function(fieldName) {
			return this.missingFields.indexOf(fieldName) > -1;
		},
		isValid: function(fieldName) {
			return (
				this.validatedFields.indexOf(fieldName) > -1 &&
				this.validFields.indexOf(fieldName) > -1
			);
		},
		isPatternMissing: function(fieldName) {
			return this.missingPatterns.indexOf(fieldName) > -1;
		},
		isPatternValid: function(fieldName) {
			return this.validPatterns.indexOf(fieldName) > -1;
		},
		validate: function(property, ifInvalid) {
			if (!ifInvalid || this.isMissing(property)) {
				this.validatedFields.push(property);
				this.missingFields = this.missingFields.filter(f => f != property);
				this.validFields = this.validFields.filter(f => f != property);
				var valid =
					this.basket.Billing[property] && this.basket.Billing[property] != "";

				if (!valid) {
					this.missingFields.push(property);
				} else {
					this.validFields.push(property);
				}
				this.reportValidation();
			}
		},
		validatePattern: function(property, ifInvalid, pattern) {
			var id = property.Id || property;
			if (!ifInvalid || this.isPatternMissing(id)) {
				pattern = pattern || property.Pattern;
				if (pattern && pattern !== "") {
					this.validatedFields.push(id);
					this.missingPatterns = this.missingPatterns.filter(f => f != id);
					this.validPatterns = this.validPatterns.filter(f => f != id);

					var value = property.Value
						? property.Value
						: this.basket.Billing[property];

					if (value) {
						var valid = value.match(pattern) != null;

						if (!valid) {
							this.missingPatterns.push(id);
						} else {
							this.validPatterns.push(id);
						}
					}
					this.reportValidation();
				}
			}
		},
		participantIsSelected: function(participant) {
			return (
				this.basket.Billing &&
				this.basket.Billing.FirstName +
					this.basket.Billing.LastName +
					this.basket.Billing.Email ==
					participant.FirstName + participant.LastName + participant.Email
			);
		},
		toggleOtherBilling: function() {
			this.basket.OtherBillingSelected = !this.basket.OtherBillingSelected;

			if (this.basket.OtherBillingSelected) {
				this.selectedParticipantIndex = -1;
				this.basket.Billing.FirstName = "";
				this.basket.Billing.LastName = "";
				this.basket.Billing.Email = "";
				this.basket.Billing.Phone = "";
				this.basket.Billing.Mobile = "";
				this.basket.Billing.CompanyName = "";
				this.basket.Billing.Address = "";
				this.basket.Billing.Zip = "";
				this.basket.Billing.City = "";

                this.saveBillingDataNow();

                if (this.$store.getters.hasTriedToCheckout) {
                    this.$store.dispatch("resetTryingToCheckout");
                    this.$store.dispatch("tryingToCheckout");
                }
			}
			this.reportValidation();
		},
		selectParticipant: function() {
			let selectedParticipant = this.distinctParticipants[
				this.selectedParticipantIndex
			];
			if (selectedParticipant) {
				this.basket.Billing = JSON.parse(JSON.stringify(selectedParticipant));
				this.basket.OtherBillingSelected = false;
				this.saveBillingDataNow();
			} else {
				this.selectedParticipantIndex = -1;
			}
			this.validatedFields = [];
			this.validFields = [];
			this.validPatterns = [];
			this.missingFields = [];
			this.missingPatterns = [];
			this.validateAll();
		},
		saveBillingData: function() {
			this.reportValidation();
			this.$store.dispatch("saveBasket", this.$store.getters.basket);
		},
		saveBillingDataNow: function() {
			this.$store.dispatch("saveBasketNow", this.$store.getters.basket);
		},
		reportValidation: function() {
			var missing = this.missingFields.concat(this.missingPatterns);
			this.$store.dispatch("reportValidation", {
				olIndex: "billing",
				index: "billing",
				missingFields:
					[...new Set(missing)].length +
					(this.showChoices && !this.basket.OtherBillingSelected && this.selectedParticipantIndex == -1 ? 1 : 0)
			});
		},
		validateAll: function() {
			if (this.hasTriedToCheckout) {
				if (
					!this.showNotFilled &&
					(!this.showChoices || this.basket.OtherBillingSelected)
				) {
					this.validate("FirstName");
					this.validate("LastName");
					this.validate("Email");
					this.validatePattern("Email", false, this.emailPattern);
					this.validate("Mobile");
					this.validatePattern("Mobile", false, this.phonePattern);
					this.validate("Address");
					this.validate("Zip");
					this.validatePattern("Zip", false, this.zipPattern);
					this.validate("City");
				} else {
				}
				this.reportValidation();
			}
		}
	},
	mounted: function() {
		this.validateAll();
	},
	watch: {
		hasTriedToCheckout: function() {
			this.selectParticipant();
			this.validateAll();
		}
	}
};
</script>
